/**
 * Championship Hall - Dramatic spotlight presentation for the Hall of Champions
 * Version: 1.2.0
 * 
 * Features:
 *   - All championships on a single pole
 *   - "Lights off" silhouette mode at start
 *   - Dramatic spotlight when a championship is selected
 *   - Smooth pole sliding with physics-based banner reactions
 *   - Photo gallery support with Ken Burns effect
 * 
 * Usage:
 *   const hall = new ChampionshipHall(canvas, championships, options);
 *   await hall.ready;
 *   hall.start();
 *   hall.selectChampionship(index); // Spotlight and center a specific banner
 *   hall.clearSelection(); // Return to silhouette mode
 * 
 * Championship data format:
 *   {
 *     season: "Fall 2025",
 *     division: "Weeknight E Division",
 *     teamName: "Team Name",
 *     photos: ["url1.jpg", "url2.jpg"] // Optional array of photo URLs
 *   }
 */

class ChampionshipHall {
    constructor(canvas, championships, options = {}) {
        this.canvas = canvas;
        this.championships = championships;
        this.options = {
            bannerWidth: options.bannerWidth || 200,
            bannerHeight: options.bannerHeight || 280,
            spacing: options.spacing || 35,
            showRod: options.showRod !== false,
            showGrommets: options.showGrommets !== false,
            startDark: options.startDark !== false,
            onSelect: options.onSelect || null
        };
        
        this.cloths = [];
        this.running = false;
        this.time = 0;
        this.teamIcons = {};
        this.trophyIcons = {};
        
        // Selection state
        this.selectedIndex = -1;
        
        // Pole position (X offset for all objects)
        this.poleOffsetX = 0;
        this.targetPoleOffsetX = 0;
        this.poleVelocity = 0;
        this.poleAcceleration = 0;
        // Critically damped spring for smooth, non-bouncy motion
        this.poleDamping = 0.88;
        this.poleSpring = 0.018;
        this.poleMaxVelocity = 17;
        
        // Spotlight state
        this.spotlightIntensity = 0;
        this.targetSpotlightIntensity = 0;
        this.spotlightAnimSpeed = 0.04;
        this.spotlight = null;
        this.spotlightTarget = null;
        this.spotlightTargetX = 0;
        this.spotlightY = 300; // Start pointing up
        this.targetSpotlightY = 300;
        this.spotlightReached = false; // Track if pole has reached target
        
        // Debug values (can be updated in real-time)
        this.spotlightOnSpeed = 0.33;
        this.spotlightAngleSpeed = 0.05;
        this.spotlightStartY = 300;
        this.spotlightEndY = 80;
        this.spotlightMaxIntensity = 4.0;
        this.triggerDistance = 10; // Units - banners are ~235 apart, so 10 is reasonable
        
        // Ambient light state
        this.ambientIntensity = this.options.startDark ? 0.08 : 0.5;
        this.targetAmbientIntensity = this.options.startDark ? 0.08 : 0.5;
        this.directionalIntensity = this.options.startDark ? 0.05 : 0.7;
        this.targetDirectionalIntensity = this.options.startDark ? 0.05 : 0.7;
        
        // Scroll reactivity
        this.scrollVelocity = 0;
        this.lastScrollY = window.scrollY;
        this.scrollDecay = 0.92;
        this.scrollSensitivity = 0.15;
        this.setupScrollListener();
        
        // Unfurl animation
        this.unfurlProgress = 0;
        this.unfurlStartTime = performance.now();
        
        // Click handling
        this.raycaster = null;
        this.mouse = null;
        this.setupClickHandler();
        
        // Container for all moveable objects
        this.poleContainer = null;
        
        // Ready promise
        this.ready = this.init();
    }
    
    async init() {
        // Ensure Pirata One font is loaded
        try {
            await document.fonts.load('400 48px Pirata One');
        } catch(e) {
            console.warn('Font load error:', e);
        }
        await document.fonts.ready;
        
        // Pre-load team icons
        const uniqueIconUrls = [...new Set(this.championships.map(c => c.iconUrl).filter(Boolean))];
        await Promise.all(uniqueIconUrls.map(async (url) => {
            try {
                this.teamIcons[url] = await this.loadImage(url);
            } catch (e) {
                console.warn('Could not load team icon:', url, e);
            }
        }));
        
        // Pre-load trophy icons
        const trophyIconPaths = {
            belt: '/assets/awards/flag_belt.png',
            lisnercup: '/assets/awards/flag_lisnercup.png',
            silvercup: '/assets/awards/flag_silvercup.png'
        };
        await Promise.all(Object.entries(trophyIconPaths).map(async ([key, url]) => {
            try {
                this.trophyIcons[key] = await this.loadImage(url);
            } catch (e) {
                console.warn('Could not load trophy icon:', key, url, e);
            }
        }));
        
        const { bannerWidth, bannerHeight, spacing } = this.options;
        const numBanners = this.championships.length;
        
        // Calculate dimensions - wider to allow for pole movement
        const contentWidth = Math.max(400, numBanners * (bannerWidth + spacing) - spacing + 100);
        const height = bannerHeight + 80;
        
        // Canvas should be viewport width to allow pole sliding
        const viewportWidth = Math.max(window.innerWidth, 800);
        
        this.canvas.width = viewportWidth;
        this.canvas.height = height;
        
        // Store banner positions relative to pole center
        this.bannerPositions = [];
        const startX = -(numBanners - 1) * (bannerWidth + spacing) / 2;
        for (let i = 0; i < numBanners; i++) {
            this.bannerPositions.push(startX + i * (bannerWidth + spacing));
        }
        
        // Three.js setup
        this.scene = new THREE.Scene();
        
        // Orthographic camera - zoomed in for bigger banners
        const aspect = viewportWidth / height;
        const frustumSize = height * 1.3; // Smaller frustum = zoomed in
        
        this.frustumSize = frustumSize;
        this.aspect = aspect;
        
        this.camera = new THREE.OrthographicCamera(
            -frustumSize * aspect / 2, 
            frustumSize * aspect / 2, 
            frustumSize / 2, 
            -frustumSize / 2, 
            0.1, 
            1000
        );
        
        this.camera.position.set(0, -30, 450);
        this.camera.lookAt(0, -height / 3, 0);
        
        this.renderer = new THREE.WebGLRenderer({ 
            canvas: this.canvas, 
            alpha: true,
            antialias: true 
        });
        this.renderer.setSize(viewportWidth, height);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.renderer.setClearColor(0x000000, 0);
        
        // Setup raycaster for click detection
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
        
        // Create container for the entire pole system
        this.poleContainer = new THREE.Group();
        this.scene.add(this.poleContainer);
        
        // LIGHTING - Start dark for silhouette effect
        this.ambientLight = new THREE.AmbientLight(0xffffff, this.ambientIntensity);
        this.scene.add(this.ambientLight);
        
        this.directionalLight = new THREE.DirectionalLight(0xffffff, this.directionalIntensity);
        this.directionalLight.position.set(0, 0.5, 1);
        this.scene.add(this.directionalLight);
        
        this.sideLight = new THREE.DirectionalLight(0xffffff, this.options.startDark ? 0.03 : 0.3);
        this.sideLight.position.set(1, 0, 0.5);
        this.scene.add(this.sideLight);
        
        this.backLight = new THREE.DirectionalLight(0x6688aa, this.options.startDark ? 0.02 : 0.2);
        this.backLight.position.set(0, 0, -1);
        this.scene.add(this.backLight);
        
        // SPOTLIGHT - For dramatic reveal (starts pointing up, angles down when pole reaches target)
        this.spotlight = new THREE.SpotLight(0xfff8e1, 0, 600, Math.PI / 7, 0.4, 1);
        this.spotlight.position.set(0, this.spotlightStartY, 350); // Start pointing up
        this.scene.add(this.spotlight);
        
        // Spotlight target (follows selected banner)
        this.spotlightTarget = new THREE.Object3D();
        this.spotlightTarget.position.set(0, -height / 2.5, 0); // Slightly lower target
        this.scene.add(this.spotlightTarget);
        this.spotlight.target = this.spotlightTarget;
        
        // Secondary rim light
        this.rimLight = new THREE.SpotLight(0x88ccff, 0, 500, Math.PI / 5, 0.6, 1);
        this.rimLight.position.set(0, -150, -250);
        this.scene.add(this.rimLight);
        
        // BOKEH PARTICLES - Floating dust in the spotlight
        this.createBokehParticles();
        
        // Create cloth banners (added to poleContainer)
        this.championships.forEach((champ, index) => {
            const x = this.bannerPositions[index];
            const cloth = this.createClothBanner(champ, x, 0, bannerWidth, bannerHeight, index);
            this.cloths.push(cloth);
            
            if (this.options.showGrommets) {
                const grommetRadius = 7;
                const grommetGeometry = new THREE.TorusGeometry(grommetRadius, 2.5, 8, 16);
                const grommetMaterial = new THREE.MeshStandardMaterial({ 
                    color: 0xC0C0C0, 
                    metalness: 0.8, 
                    roughness: 0.3 
                });
                
                const leftGrommet = new THREE.Mesh(grommetGeometry, grommetMaterial);
                leftGrommet.position.set(x - bannerWidth/2 + 18, 5, 2);
                leftGrommet.rotation.y = Math.PI / 2;
                this.poleContainer.add(leftGrommet);
                
                const rightGrommet = new THREE.Mesh(grommetGeometry, grommetMaterial);
                rightGrommet.position.set(x + bannerWidth/2 - 18, 5, 2);
                rightGrommet.rotation.y = Math.PI / 2;
                this.poleContainer.add(rightGrommet);
            }
        });
        
        if (this.options.showRod) {
            const ropeWidth = numBanners * (bannerWidth + spacing) + 80;
            const ropeGeometry = new THREE.CylinderGeometry(4, 4, ropeWidth, 16);
            const ropeMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x4a3728,
                metalness: 0.1, 
                roughness: 0.9 
            });
            this.rope = new THREE.Mesh(ropeGeometry, ropeMaterial);
            this.rope.rotation.z = Math.PI / 2;
            this.rope.position.set(0, 12, -5);
            this.poleContainer.add(this.rope);
            
            const capGeometry = new THREE.SphereGeometry(6, 16, 16);
            const capMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x3d2817, 
                metalness: 0.2, 
                roughness: 0.7 
            });
            this.leftCap = new THREE.Mesh(capGeometry, capMaterial);
            this.leftCap.position.set(-ropeWidth/2, 12, -5);
            this.poleContainer.add(this.leftCap);
            
            this.rightCap = new THREE.Mesh(capGeometry, capMaterial);
            this.rightCap.position.set(ropeWidth/2, 12, -5);
            this.poleContainer.add(this.rightCap);
        }
    }
    
    setupScrollListener() {
        this.scrollHandler = () => {
            const currentScrollY = window.scrollY;
            const delta = currentScrollY - this.lastScrollY;
            this.scrollVelocity += delta * this.scrollSensitivity;
            this.lastScrollY = currentScrollY;
        };
        window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
    
    setupClickHandler() {
        this.clickHandler = (event) => {
            if (!this.raycaster || !this.camera) return;
            
            const rect = this.canvas.getBoundingClientRect();
            this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
            
            this.raycaster.setFromCamera(this.mouse, this.camera);
            
            const meshes = this.cloths.map(c => c.mesh);
            const intersects = this.raycaster.intersectObjects(meshes);
            
            if (intersects.length > 0) {
                const clickedMesh = intersects[0].object;
                const index = meshes.indexOf(clickedMesh);
                if (index !== -1) {
                    this.toggleSelection(index);
                }
            }
        };
        this.canvas.addEventListener('click', this.clickHandler);
        this.canvas.style.cursor = 'pointer';
    }
    
    getTrophyIconForDivision(division) {
        if (!division || !this.trophyIcons) return null;
        
        const divLower = division.toLowerCase();
        
        if (divLower.includes('peconic')) {
            return this.trophyIcons.silvercup;
        }
        if (divLower.includes('premier')) {
            return this.trophyIcons.lisnercup;
        }
        if (divLower.includes('weeknight d') && !divLower.includes('e/d') && !divLower.includes('e d')) {
            return this.trophyIcons.lisnercup;
        }
        if (divLower.includes('weekend') || divLower.includes('weeknight e') || divLower.includes('e/d')) {
            return this.trophyIcons.belt;
        }
        
        return this.trophyIcons.belt;
    }
    
    loadImage(url) {
        return new Promise((resolve) => {
            const isSameOrigin = !url.includes('://') || url.startsWith(window.location.origin);
            
            const img = new Image();
            if (isSameOrigin) {
                img.onload = () => resolve(img);
                img.onerror = () => resolve(null);
            } else {
                img.crossOrigin = 'anonymous';
                img.onload = () => resolve(img);
                // S3 only sends CORS headers (and Vary: Origin) when the request has an
                // Origin, so if the page already showed this logo in a plain <img>, the
                // browser reuses that header-less cached copy and the CORS load fails.
                // Retry once with a cache-busting param to force a fresh CORS fetch.
                // (Not "cors" — S3 treats ?cors as the bucket-CORS subresource and 403s.)
                img.onerror = () => {
                    const retry = new Image();
                    retry.crossOrigin = 'anonymous';
                    retry.onload = () => resolve(retry);
                    retry.onerror = () => resolve(null);
                    retry.src = url + (url.includes('?') ? '&' : '?') + 'xo=1';
                };
            }
            img.src = url;
        });
    }
    
    createClothBanner(champ, originX, originY, width, height, index) {
        const segmentsX = 32;
        const segmentsY = 40;
        
        const scale = Math.min(window.devicePixelRatio || 1, 2) * 4;
        // Create two textures so we can reveal the watermark only when the spotlight is on:
        // - baseTexture: no team-icon watermark
        // - watermarkTexture: includes team-icon watermark (same as before)
        const teamIcon = champ.iconUrl ? this.teamIcons[champ.iconUrl] : null;
        const baseTexture = this.createBannerTexture(champ, width * scale, height * scale, { includeTeamIcon: false });
        const watermarkTexture = teamIcon
            ? this.createBannerTexture(champ, width * scale, height * scale, { includeTeamIcon: true })
            : null;
        
        const geometry = new THREE.PlaneGeometry(width, height, segmentsX, segmentsY);
        const material = new THREE.MeshPhongMaterial({
            map: baseTexture,
            side: THREE.DoubleSide,
            transparent: true,
            shininess: 5,
            specular: 0x222222
        });
        
        // If this banner has a team icon watermark, blend it in ONLY under the spotlight.
        if (watermarkTexture) {
            material.userData.watermarkTexture = watermarkTexture;
            material.userData.spotlightUniforms = null;
            
            material.onBeforeCompile = (shader) => {
                shader.uniforms.uWatermarkMap = { value: watermarkTexture };
                shader.uniforms.uSpotlightX = { value: 0 };
                shader.uniforms.uSpotlightIntensity = { value: 0 };
                shader.uniforms.uRevealWidth = { value: 280.0 }; // How wide the reveal is across the banner row
                
                material.userData.spotlightUniforms = shader.uniforms;
                
                // Pass world X into fragment shader so reveal follows spotlight position
                shader.vertexShader = shader.vertexShader
                    .replace(
                        'void main() {',
                        'varying float vWorldX;\nvoid main() {'
                    )
                    .replace(
                        '#include <begin_vertex>',
                        `#include <begin_vertex>
  // Compute world position unconditionally (worldPosition isn't always declared in Three shader chunks)
  vec4 lihWorldPos = vec4( transformed, 1.0 );
  #ifdef USE_INSTANCING
    lihWorldPos = instanceMatrix * lihWorldPos;
  #endif
  lihWorldPos = modelMatrix * lihWorldPos;
  vWorldX = lihWorldPos.x;`
                    );
                
                shader.fragmentShader = shader.fragmentShader
                    .replace(
                        'void main() {',
                        'uniform sampler2D uWatermarkMap;\nuniform float uSpotlightX;\nuniform float uSpotlightIntensity;\nuniform float uRevealWidth;\nvarying float vWorldX;\nvoid main() {'
                    )
                    .replace(
                        '#include <map_fragment>',
                        `
#ifdef USE_MAP
    vec4 baseTexelColor = texture2D( map, vUv );
    vec4 watermarkTexelColor = texture2D( uWatermarkMap, vUv );
    
    float dist = abs(vWorldX - uSpotlightX);
    float spotlightMask = 1.0 - smoothstep(0.0, uRevealWidth, dist);
    float iconMask = clamp(uSpotlightIntensity, 0.0, 1.0) * spotlightMask;
    
    vec4 texelColor = mix(baseTexelColor, watermarkTexelColor, iconMask);
    texelColor = mapTexelToLinear( texelColor );
    diffuseColor *= texelColor;
#endif
                        `.trim()
                    );
            };
        }
        
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.set(originX, originY - height / 2, 0);
        this.poleContainer.add(mesh);
        
        return {
            mesh,
            geometry,
            segmentsX,
            segmentsY,
            width,
            height,
            phaseOffset: index * 2.0,
            originX,
            // Physics state for movement reaction
            swayOffset: 0,
            swayVelocity: 0
        };
    }
    
    createBannerTexture(champ, width, height, options = { includeTeamIcon: true }) {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        const bgColor = champ.flagBg || champ.mainColor || '#1a472a';
        const trimColor = champ.flagTrim || champ.accentColor || '#e91e63';
        const text1Color = champ.flagText1 || '#ffffff';
        const text2Color = champ.flagText2 || champ.accentColor || '#ffd700';
        
        const trimWidth = width * 0.05;
        const trimHighlight = this.lightenColor(trimColor, 40);
        const trimShadow = this.darkenColor(trimColor, 30);
        
        const drawBannerPath = (inset = 0) => {
            ctx.beginPath();
            ctx.moveTo(inset, inset);
            ctx.lineTo(width - inset, inset);
            ctx.lineTo(width - inset, height * 0.82 - inset);
            ctx.lineTo(width / 2, height - inset * 2);
            ctx.lineTo(inset, height * 0.82 - inset);
            ctx.closePath();
        };
        
        drawBannerPath(0);
        const outerTrimGradient = ctx.createLinearGradient(0, 0, trimWidth, 0);
        outerTrimGradient.addColorStop(0, trimHighlight);
        outerTrimGradient.addColorStop(0.5, trimColor);
        outerTrimGradient.addColorStop(1, trimShadow);
        ctx.fillStyle = outerTrimGradient;
        ctx.fill();
        
        drawBannerPath(trimWidth);
        const fabricGradient = ctx.createLinearGradient(0, 0, 0, height);
        fabricGradient.addColorStop(0, this.lightenColor(bgColor, 10));
        fabricGradient.addColorStop(0.3, bgColor);
        fabricGradient.addColorStop(0.7, this.darkenColor(bgColor, 15));
        fabricGradient.addColorStop(1, this.darkenColor(bgColor, 25));
        ctx.fillStyle = fabricGradient;
        ctx.fill();
        
        const cfg = {
            trophyY: 0.5,
            trophySize: 0.44,
            iconSize: 1.5,
            iconY: 0.06,
            divisionY: 0.22,
            seasonY: 0.12,
            divisionFontSize: 0.14,
            seasonFontSize: 0.18
        };
        
        const teamIcon = champ.iconUrl ? this.teamIcons[champ.iconUrl] : null;
        if (options.includeTeamIcon && teamIcon) {
            ctx.save();
            drawBannerPath(trimWidth);
            ctx.clip();
            ctx.globalAlpha = 0.4;
            
            const iconSizeVal = width * cfg.iconSize;
            const iconX = (width - iconSizeVal) / 2;
            const iconYVal = height * cfg.iconY;
            
            ctx.drawImage(teamIcon, iconX, iconYVal, iconSizeVal, iconSizeVal);
            ctx.globalAlpha = 1;
            ctx.restore();
        }
        
        const shine = ctx.createLinearGradient(width * 0.3, 0, width * 0.7, 0);
        shine.addColorStop(0, 'rgba(255,255,255,0)');
        shine.addColorStop(0.5, 'rgba(255,255,255,0.1)');
        shine.addColorStop(1, 'rgba(255,255,255,0)');
        drawBannerPath(trimWidth);
        ctx.fillStyle = shine;
        ctx.fill();
        
        const contentTop = trimWidth + height * 0.08;
        
        const trophyIcon = this.getTrophyIconForDivision(champ.division);
        if (trophyIcon) {
            ctx.save();
            ctx.globalAlpha = 1.0;
            
            const trophySize = width * cfg.trophySize * 1.3;
            // Preserve trophy image aspect ratio (some trophy PNGs are not square)
            const srcW = trophyIcon.naturalWidth || trophyIcon.width;
            const srcH = trophyIcon.naturalHeight || trophyIcon.height;
            const aspect = srcW && srcH ? (srcW / srcH) : 1;
            
            const centerX = width / 2;
            const centerY = contentTop + height * cfg.trophyY;
            
            let drawW = trophySize;
            let drawH = trophySize;
            if (aspect >= 1) {
                // Wider than tall: fit width, scale height
                drawH = trophySize / aspect;
            } else {
                // Taller than wide: fit height, scale width
                drawW = trophySize * aspect;
            }
            
            const trophyX = centerX - drawW / 2;
            const trophyY = centerY - drawH / 2;
            
            ctx.drawImage(trophyIcon, trophyX, trophyY, drawW, drawH);
            
            ctx.filter = 'none';
            ctx.restore();
        }
        
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = text1Color;
        ctx.font = `${width * cfg.divisionFontSize}px 'Pirata One', cursive`;
        ctx.shadowColor = 'rgba(0,0,0,0.6)';
        ctx.shadowBlur = 4;
        ctx.shadowOffsetY = 2;
        
        const divisionText = champ.division || 'CHAMPIONS';
        const words = divisionText.split(' ');
        let line = '';
        let lineY = contentTop + height * cfg.divisionY;
        const lineHeight = width * (cfg.divisionFontSize + 0.01);
        
        words.forEach((word, i) => {
            const testLine = line + (line ? ' ' : '') + word;
            if (ctx.measureText(testLine).width > width * 0.75 && i > 0) {
                ctx.fillText(line, width / 2, lineY);
                line = word;
                lineY += lineHeight;
            } else {
                line = testLine;
            }
        });
        ctx.fillText(line, width / 2, lineY);
        
        ctx.fillStyle = text2Color;
        ctx.font = `${width * cfg.seasonFontSize}px 'Pirata One', cursive`;
        ctx.shadowColor = 'rgba(0,0,0,0.7)';
        ctx.shadowBlur = 6;
        ctx.fillText(champ.season, width / 2, contentTop + height * cfg.seasonY);
        
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetY = 0;
        
        ctx.save();
        drawBannerPath(0);
        ctx.clip();
        
        const threadSpacing = 5;
        
        for (let y = 0; y < height; y += threadSpacing) {
            for (let x = 0; x < width; x += threadSpacing) {
                const isLight = ((x / threadSpacing) + (y / threadSpacing)) % 2 === 0;
                ctx.globalAlpha = 0.15;
                ctx.fillStyle = isLight ? '#ffffff' : '#000000';
                ctx.fillRect(x, y, threadSpacing - 1, threadSpacing - 1);
            }
        }
        
        ctx.globalAlpha = 0.25;
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 0.5;
        for (let y = 0; y < height; y += threadSpacing) {
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(width, y);
            ctx.stroke();
        }
        for (let x = 0; x < width; x += threadSpacing) {
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, height);
            ctx.stroke();
        }
        
        ctx.globalAlpha = 0.2;
        for (let i = 0; i < 6000; i++) {
            const nx = Math.random() * width;
            const ny = Math.random() * height;
            const size = Math.random() * 2 + 0.5;
            ctx.fillStyle = Math.random() > 0.5 ? '#ffffff' : '#000000';
            ctx.fillRect(nx, ny, size, size);
        }
        
        ctx.globalAlpha = 1;
        ctx.restore();
        
        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;
        return texture;
    }
    
    darkenColor(hex, percent) {
        hex = hex.replace('#', '');
        let r = parseInt(hex.substring(0, 2), 16);
        let g = parseInt(hex.substring(2, 4), 16);
        let b = parseInt(hex.substring(4, 6), 16);
        r = Math.max(0, Math.floor(r * (100 - percent) / 100));
        g = Math.max(0, Math.floor(g * (100 - percent) / 100));
        b = Math.max(0, Math.floor(b * (100 - percent) / 100));
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }
    
    lightenColor(hex, percent) {
        hex = hex.replace('#', '');
        let r = parseInt(hex.substring(0, 2), 16);
        let g = parseInt(hex.substring(2, 4), 16);
        let b = parseInt(hex.substring(4, 6), 16);
        r = Math.min(255, Math.floor(r + (255 - r) * percent / 100));
        g = Math.min(255, Math.floor(g + (255 - g) * percent / 100));
        b = Math.min(255, Math.floor(b + (255 - b) * percent / 100));
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }
    
    /**
     * Create bokeh/dust particles that float in the spotlight
     */
    createBokehParticles() {
        const particleCount = 15;
        const positions = new Float32Array(particleCount * 3);
        const sizes = new Float32Array(particleCount);
        const velocities = [];
        const opacities = new Float32Array(particleCount);
        
        // Vertical placement: keep bokeh/dust around the banner flags (and slightly below),
        // instead of floating too high above the pole.
        const particleTopY = 60;      // slightly above banner top (banner top is ~0)
        const particleBottomY = -320; // a bit below banner bottom (banner bottom is ~-280)
        
        // Make particle "cone" a bit wider to better match the spotlight width.
        const particleWidthScale = 1.35;
        
        // Create particle texture (soft circle with glow)
        const canvas = document.createElement('canvas');
        canvas.width = 64;
        canvas.height = 64;
        const ctx = canvas.getContext('2d');
        
        // Radial gradient for very soft, blurry bokeh look
        const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
        gradient.addColorStop(0, 'rgba(255, 248, 220, 1)');
        gradient.addColorStop(0.2, 'rgba(255, 248, 220, 0.8)');
        gradient.addColorStop(0.5, 'rgba(255, 248, 220, 0.3)');
        gradient.addColorStop(0, 'rgba(255, 248, 220, 0.6)');
        gradient.addColorStop(0.1, 'rgba(255, 248, 220, 0.4)');
        gradient.addColorStop(0.3, 'rgba(255, 248, 220, 0.2)');
        gradient.addColorStop(0.6, 'rgba(255, 248, 220, 0.08)');
        gradient.addColorStop(1, 'rgba(255, 248, 220, 0)');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 64, 64);
        
        const particleTexture = new THREE.CanvasTexture(canvas);
        
        // Initialize particles in a cone shape (spotlight area) - both in front and behind banners
        for (let i = 0; i < particleCount; i++) {
            // Spread particles in a cone from spotlight to banner area
            const t = Math.random(); // 0 = near spotlight, 1 = near banner
            const spread = (100 + t * 200) * particleWidthScale; // Cone widens toward banner
            
            positions[i * 3] = (Math.random() - 0.5) * spread; // X
            // Y: from around the top of the flags down a bit past the bottom edge
            positions[i * 3 + 1] = particleTopY + t * (particleBottomY - particleTopY) + (Math.random() - 0.5) * 80;
            // Z: spread from behind banners (-100) to in front (+250)
            positions[i * 3 + 2] = (Math.random() - 0.3) * 300; // Some behind, most in front
            
            sizes[i] = 8 + Math.random() * 16; // Larger, softer particles
            opacities[i] = 0.3 + Math.random() * 0.5;
            
            // Random slow drift velocities
            velocities.push({
                x: (Math.random() - 0.5) * 0.3,
                y: (Math.random() - 0.5) * 0.2 - 0.1, // Slight downward drift
                z: (Math.random() - 0.5) * 0.2
            });
        }
        
        this.particleVelocities = velocities;
        this.particleBasePositions = positions.slice(); // Store original positions
        
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
        
        // Custom shader material for particles
        const material = new THREE.ShaderMaterial({
            uniforms: {
                uTexture: { value: particleTexture },
                uOpacity: { value: 0 }, // Start invisible
                uSpotlightX: { value: 0 },
                uSpotlightIntensity: { value: 0 }
            },
            vertexShader: `
                attribute float size;
                varying float vDistance;
                uniform float uSpotlightX;
                
                void main() {
                    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                    
                    // Calculate distance from spotlight center (X axis)
                    vDistance = abs(position.x - uSpotlightX);
                    
                    gl_PointSize = size * (300.0 / -mvPosition.z);
                    gl_Position = projectionMatrix * mvPosition;
                }
            `,
            fragmentShader: `
                uniform sampler2D uTexture;
                uniform float uOpacity;
                uniform float uSpotlightIntensity;
                varying float vDistance;
                
                void main() {
                    // Fade out particles far from spotlight center
                    float spotlightFade = 1.0 - smoothstep(0.0, 260.0, vDistance);
                    
                    vec4 texColor = texture2D(uTexture, gl_PointCoord);
                    float alpha = texColor.a * uOpacity * spotlightFade * uSpotlightIntensity;
                    
                    gl_FragColor = vec4(texColor.rgb, alpha);
                }
            `,
            transparent: true,
            blending: THREE.AdditiveBlending,
            depthWrite: false
        });
        
        this.particles = new THREE.Points(geometry, material);
        this.scene.add(this.particles);
    }
    
    /**
     * Update bokeh particles animation
     */
    updateParticles(poleVelocity = 0) {
        if (!this.particles) return;
        
        // Track turbulence from pole movement (decays slowly over time)
        if (!this.particleTurbulence) this.particleTurbulence = 0;
        this.particleTurbulence += Math.abs(poleVelocity) * 0.8; // Add turbulence from movement
        this.particleTurbulence *= 0.975; // Decay for natural settling
        
        const positions = this.particles.geometry.attributes.position.array;
        const time = performance.now() * 0.001;
        const turbulence = Math.min(this.particleTurbulence, 30); // Cap turbulence
        
        for (let i = 0; i < positions.length / 3; i++) {
            const vel = this.particleVelocities[i];
            const baseX = this.particleBasePositions[i * 3];
            const baseY = this.particleBasePositions[i * 3 + 1];
            const baseZ = this.particleBasePositions[i * 3 + 2];
            
            // Gentle floating motion with sine waves
            const floatX = Math.sin(time * 0.5 + i * 0.1) * 10;
            const floatY = Math.sin(time * 0.3 + i * 0.2) * 8;
            const floatZ = Math.sin(time * 0.4 + i * 0.15) * 5;
            
            // Add turbulence from banner movement (swirl effect)
            const turbX = Math.sin(time * 2 + i * 0.5) * turbulence * (vel.x > 0 ? 1 : -1);
            const turbY = Math.cos(time * 1.5 + i * 0.3) * turbulence * 0.5;
            const turbZ = Math.sin(time * 1.8 + i * 0.4) * turbulence * 0.3;
            
            positions[i * 3] = baseX + floatX + vel.x * time * 10 + turbX - poleVelocity * 2;
            positions[i * 3 + 1] = baseY + floatY + vel.y * time * 10 + turbY;
            positions[i * 3 + 2] = baseZ + floatZ + turbZ;
            
            // Wrap particles that drift too far
            if (positions[i * 3] > 520) positions[i * 3] = -520;
            if (positions[i * 3] < -520) positions[i * 3] = 520;
            // Allow particles to live around/below banners before wrapping back up.
            if (positions[i * 3 + 1] < -420) positions[i * 3 + 1] = 80;
        }
        
        this.particles.geometry.attributes.position.needsUpdate = true;
        
        // Update shader uniforms
        const material = this.particles.material;
        // Slightly brighter particles
        material.uniforms.uOpacity.value = this.spotlightIntensity > 0.1 ? 0.9 : 0;
        material.uniforms.uSpotlightX.value = this.spotlightTargetX + this.poleOffsetX;
        material.uniforms.uSpotlightIntensity.value = Math.min(1, this.spotlightIntensity / 1.6);
    }
    
    /**
     * Select a championship by index - centers it and turns on spotlight
     */
    selectChampionship(index) {
        if (index < 0 || index >= this.championships.length) return;
        
        this.selectedIndex = index;
        
        // Target pole offset to center the selected banner (negative because we move pole left to show right banners)
        this.targetPoleOffsetX = -this.bannerPositions[index];
        
        // Set spotlight target position (in world space, considering pole offset)
        this.spotlightTargetX = this.bannerPositions[index];
        
        // Start spotlight animation immediately - starts pointing up, angles down as pole moves
        this.spotlightReached = false;
        this.spotlightY = this.spotlightStartY; // Start pointing up (from debug value)
        this.targetSpotlightY = this.spotlightStartY; // Keep pointing up until pole reaches target
        this.targetSpotlightIntensity = 0; // Start off
        if (this.spotlight) {
            this.spotlight.position.y = this.spotlightStartY;
            this.spotlight.intensity = 0;
        }
        
        // Keep ambient low
        this.targetAmbientIntensity = 0.10;
        this.targetDirectionalIntensity = 0.06;
        
        // Callback
        if (this.options.onSelect) {
            this.options.onSelect(index, this.championships[index]);
        }
    }
    
    /**
     * Clear selection - return to silhouette mode
     */
    clearSelection() {
        this.selectedIndex = -1;
        this.targetPoleOffsetX = 0;
        this.targetSpotlightIntensity = 0;
        this.targetAmbientIntensity = 0.08;
        this.targetDirectionalIntensity = 0.05;
        this.spotlightReached = false;
        this.targetSpotlightY = this.spotlightStartY; // Reset to pointing up
        
        if (this.options.onSelect) {
            this.options.onSelect(-1, null);
        }
    }
    
    /**
     * Toggle selection
     */
    toggleSelection(index) {
        if (this.selectedIndex === index) {
            this.clearSelection();
        } else {
            this.selectChampionship(index);
        }
    }
    
    /**
     * Update debug values in real-time
     */
    updateDebugValues(values) {
        if (!values) return;
        
        if (values.poleSpring !== undefined) this.poleSpring = values.poleSpring;
        if (values.poleDamping !== undefined) this.poleDamping = values.poleDamping;
        if (values.poleMaxVelocity !== undefined) this.poleMaxVelocity = values.poleMaxVelocity;
        if (values.spotlightOnSpeed !== undefined) this.spotlightOnSpeed = values.spotlightOnSpeed;
        if (values.spotlightAngleSpeed !== undefined) this.spotlightAngleSpeed = values.spotlightAngleSpeed;
        if (values.spotlightStartY !== undefined) {
            const oldStartY = this.spotlightStartY;
            this.spotlightStartY = values.spotlightStartY;
            if (this.spotlight && !this.spotlightReached && Math.abs(this.spotlightY - oldStartY) < 20) {
                this.spotlightY = values.spotlightStartY;
                this.targetSpotlightY = values.spotlightStartY;
                this.spotlight.position.y = values.spotlightStartY;
            }
        }
        if (values.spotlightEndY !== undefined) {
            this.spotlightEndY = values.spotlightEndY;
            if (this.spotlight && this.spotlightReached && Math.abs(this.spotlightY - this.spotlightEndY) < 20) {
                this.targetSpotlightY = values.spotlightEndY;
            }
        }
        if (values.spotlightIntensity !== undefined) {
            this.spotlightMaxIntensity = values.spotlightIntensity;
            if (this.spotlightReached && this.targetSpotlightIntensity > 0) {
                this.targetSpotlightIntensity = values.spotlightIntensity;
            }
        }
        if (values.triggerDistance !== undefined) this.triggerDistance = values.triggerDistance;
    }
    
    updateCloth(cloth, poleVelocity) {
        const { segmentsX, segmentsY, width, height, phaseOffset } = cloth;
        const positions = cloth.geometry.attributes.position;
        const time = performance.now() / 1000;
        
        const clampedScrollVelocity = Math.max(-80, Math.min(80, this.scrollVelocity));
        const unfurl = Math.max(0, Math.min(1, this.unfurlProgress));
        
        // Smooth cloth trailing effect based on pole velocity (no bounce)
        // The cloth trails behind the movement direction
        const targetSway = -poleVelocity * 4; // Opposite direction of movement
        
        // Smoothly interpolate towards target (no spring, just smooth follow)
        cloth.swayOffset += (targetSway - cloth.swayOffset) * 0.08;
        
        // Gentle return to rest when not moving
        if (Math.abs(poleVelocity) < 0.1) {
            cloth.swayOffset *= 0.96;
        }
        
        // Clamp sway
        cloth.swayOffset = Math.max(-35, Math.min(35, cloth.swayOffset));
        
        for (let j = 0; j <= segmentsY; j++) {
            for (let i = 0; i <= segmentsX; i++) {
                const idx = j * (segmentsX + 1) + i;
                
                const u = i / segmentsX;
                const v = j / segmentsY;
                
                const baseX = (u - 0.5) * width;
                const baseY = (0.5 - v) * height;
                
                const rowDelay = v * 0.6;
                const rowUnfurl = Math.max(0, Math.min(1, (unfurl - rowDelay) / (1 - rowDelay + 0.01)));
                const easedRowUnfurl = rowUnfurl * rowUnfurl * (3 - 2 * rowUnfurl);
                
                const topY = height * 0.48;
                const y = topY + (baseY - topY) * easedRowUnfurl;
                
                const unfurlSway = (1 - unfurl) * Math.sin(time * 1.5 + phaseOffset + v * 2) * v * 20;
                const horizontalDrift = (1 - easedRowUnfurl) * Math.sin(time * 0.8 + phaseOffset * 1.5 + v * 3) * 8;
                
                // Add pole movement sway - increases with depth (v)
                const movementSway = cloth.swayOffset * v * v;
                
                const x = baseX + horizontalDrift + movementSway;
                
                const verticalFactor = v * v * easedRowUnfurl;
                const edgeFactor = 1.0 + Math.sin(u * Math.PI) * 0.5;
                const intensity = verticalFactor * edgeFactor;
                
                const wave1 = Math.sin(time * 1.2 + phaseOffset + u * 3 - v * 5) * 12;
                const wave2 = Math.sin(time * 2.0 + phaseOffset * 0.7 - v * 8 + u * 2) * 6;
                const wave3 = Math.cos(time * 0.8 + phaseOffset * 1.3 + u * 4 + v * 3) * 4;
                
                const scrollWave = -clampedScrollVelocity * verticalFactor * 0.8;
                
                const billowPhase = time * 2 + phaseOffset + v * 4;
                const billow = (1 - unfurl) * Math.sin(billowPhase) * (1 - Math.abs(u - 0.5) * 2) * 25;
                
                // Add depth variation from pole movement
                const moveDepth = cloth.swayOffset * v * 0.3;
                
                const z = (wave1 + wave2 + wave3) * intensity + scrollWave + unfurlSway + billow + moveDepth;
                
                positions.setXYZ(idx, x, y, z);
            }
        }
        positions.needsUpdate = true;
        cloth.geometry.computeVertexNormals();
    }
    
    animate() {
        if (!this.running || !this.renderer) return;
        
        // Decay scroll velocity
        this.scrollVelocity *= this.scrollDecay;
        if (Math.abs(this.scrollVelocity) < 0.01) this.scrollVelocity = 0;
        
        // Unfurl animation
        if (this.unfurlProgress < 1) {
            const elapsed = (performance.now() - this.unfurlStartTime) / 1000;
            const duration = 2.5;
            
            if (elapsed < duration) {
                const t = elapsed / duration;
                this.unfurlProgress = 1 - Math.pow(1 - t, 3);
            } else {
                this.unfurlProgress = 1;
            }
        }
        
        // Smooth pole movement - critically damped (no bounce)
        const poleError = this.targetPoleOffsetX - this.poleOffsetX;
        
        // Two thresholds: one for triggering spotlight (larger), one for stopping physics (smaller)
        const closeEnoughForSpotlight = Math.abs(poleError) < this.triggerDistance;
        const closeEnoughToStop = Math.abs(poleError) < 0.5; // Small threshold for physics snap
        
        // Trigger spotlight the moment we're close enough
        const justReached = !this.spotlightReached && closeEnoughForSpotlight;
        
        // Always apply physics unless we're REALLY close
        if (!closeEnoughToStop) {
            this.poleAcceleration = poleError * this.poleSpring;
            this.poleVelocity += this.poleAcceleration;
            this.poleVelocity *= this.poleDamping;
            
            // Cap velocity to prevent too-fast movement
            this.poleVelocity = Math.max(-this.poleMaxVelocity, Math.min(this.poleMaxVelocity, this.poleVelocity));
        } else {
            // Snap to target when very close (prevents micro-oscillations)
            this.poleOffsetX = this.targetPoleOffsetX;
            this.poleVelocity *= 0.5; // Quickly settle velocity
            this.poleAcceleration = 0;
        }
        
        const prevPoleOffset = this.poleOffsetX;
        this.poleOffsetX += this.poleVelocity;
        
        // Track velocity for cloth physics (how fast pole is moving)
        const actualVelocity = this.poleOffsetX - prevPoleOffset;
        
        // Update pole container position
        if (this.poleContainer) {
            this.poleContainer.position.x = this.poleOffsetX;
        }
        
        // Update spotlight target position (moves with pole)
        if (this.spotlightTarget && this.selectedIndex >= 0) {
            this.spotlightTarget.position.x = this.spotlightTargetX + this.poleOffsetX;
        }
        
        // Animate spotlight Y position - trigger when pole first reaches target
        if (this.selectedIndex >= 0) {
            // When pole first reaches target, trigger quick spotlight animation
            if (justReached) {
                this.spotlightReached = true;
                this.targetSpotlightY = this.spotlightEndY; // Angle down
                this.targetSpotlightIntensity = this.spotlightMaxIntensity; // Turn on quickly
            }
            
            // Animate spotlight Y position (uses debug speed)
            const spotlightYDiff = this.targetSpotlightY - this.spotlightY;
            if (Math.abs(spotlightYDiff) > 0.5) {
                this.spotlightY += spotlightYDiff * this.spotlightAngleSpeed;
                if (this.spotlight) {
                    this.spotlight.position.y = this.spotlightY;
                }
            }
        }
        
        // Animate spotlight intensity (uses debug speed)
        const spotlightDiff = this.targetSpotlightIntensity - this.spotlightIntensity;
        if (Math.abs(spotlightDiff) > 0.01) {
            // Use debug speed when turning on, default speed when turning off
            const speed = this.spotlightIntensity < this.targetSpotlightIntensity ? this.spotlightOnSpeed : this.spotlightAnimSpeed;
            this.spotlightIntensity += spotlightDiff * speed;
            if (this.spotlight) {
                this.spotlight.intensity = this.spotlightIntensity;
            }
            this.rimLight.intensity = this.spotlightIntensity * 0.25;
        }
        
        // Animate ambient intensity
        const ambientDiff = this.targetAmbientIntensity - this.ambientIntensity;
        if (Math.abs(ambientDiff) > 0.001) {
            this.ambientIntensity += ambientDiff * this.spotlightAnimSpeed;
            this.ambientLight.intensity = this.ambientIntensity;
        }
        
        // Animate directional intensity
        const directionalDiff = this.targetDirectionalIntensity - this.directionalIntensity;
        if (Math.abs(directionalDiff) > 0.001) {
            this.directionalIntensity += directionalDiff * this.spotlightAnimSpeed;
            this.directionalLight.intensity = this.directionalIntensity;
            this.sideLight.intensity = this.directionalIntensity * 0.4;
            this.backLight.intensity = this.directionalIntensity * 0.3;
        }
        
        // Update cloths with pole velocity for physics reaction (gentle drag effect)
        this.cloths.forEach(cloth => this.updateCloth(cloth, actualVelocity * 3));
        
        // Update bokeh particles (pass velocity so they react to movement)
        this.updateParticles(actualVelocity);
        
        // Update spotlight-driven watermark reveal uniforms (team-icon background)
        const spotlightWorldX = this.spotlightTarget ? this.spotlightTarget.position.x : 0;
        const spotlightNorm = this.spotlightMaxIntensity ? Math.min(1, this.spotlightIntensity / this.spotlightMaxIntensity) : 0;
        this.cloths.forEach((cloth) => {
            const uniforms = cloth.mesh?.material?.userData?.spotlightUniforms;
            if (!uniforms) return;
            uniforms.uSpotlightX.value = spotlightWorldX;
            uniforms.uSpotlightIntensity.value = spotlightNorm;
        });
        
        this.renderer.render(this.scene, this.camera);
        
        this.animationId = requestAnimationFrame(() => this.animate());
    }
    
    start() {
        if (this.running || !this.renderer) return;
        this.running = true;
        // Reset unfurl animation so banners always start rolled up, even if loading took a while
        this.unfurlProgress = 0;
        this.unfurlStartTime = performance.now();
        this.animate();
    }
    
    stop() {
        this.running = false;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
        }
    }
    
    destroy() {
        this.stop();
        if (this.scrollHandler) {
            window.removeEventListener('scroll', this.scrollHandler);
        }
        if (this.clickHandler) {
            this.canvas.removeEventListener('click', this.clickHandler);
        }
        this.cloths.forEach(cloth => {
            cloth.geometry.dispose();
            cloth.mesh.material.map.dispose();
            if (cloth.mesh.material.userData && cloth.mesh.material.userData.watermarkTexture) {
                cloth.mesh.material.userData.watermarkTexture.dispose();
            }
            cloth.mesh.material.dispose();
        });
        if (this.renderer) {
            this.renderer.dispose();
        }
    }
}


/**
 * Ken Burns Photo Gallery - Dramatic photo display with pan/zoom effects
 */
class KenBurnsGallery {
    constructor(container, options = {}) {
        this.container = container;
        this.options = {
            transitionDuration: options.transitionDuration || 1500,
            displayDuration: options.displayDuration || 5000,
            zoomRange: options.zoomRange || [1.0, 1.15],
            panRange: options.panRange || 8 // percentage
        };
        
        this.photos = [];
        this.currentIndex = 0;
        this.isPlaying = false;
        this.imageElements = [];
        this.intervalId = null;
        
        this.init();
    }
    
    init() {
        // Don't override position if already set (e.g., position:absolute from CSS)
        const computedPosition = window.getComputedStyle(this.container).position;
        if (computedPosition === 'static') {
            this.container.style.position = 'relative';
        }
        this.container.style.overflow = 'hidden';
        
        // Create two image layers for crossfade
        for (let i = 0; i < 2; i++) {
            const img = document.createElement('div');
            img.className = 'ken-burns-image';
            img.style.cssText = `
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background-size: cover;
                background-position: center;
                background-repeat: no-repeat;
                opacity: 0;
                z-index: ${i + 1};
                transition: opacity ${this.options.transitionDuration}ms ease-in-out;
                will-change: transform, opacity;
            `;
            this.container.appendChild(img);
            this.imageElements.push(img);
        }
    }
    
    setPhotos(photos) {
        this.stop(); // Stop any existing slideshow
        this.photos = photos || [];
        this.currentIndex = 0;
        console.log('KenBurnsGallery: setPhotos called with', this.photos.length, 'photos');
        
        if (this.photos.length > 0) {
            this.showImage(0, 0);
        }
    }
    
    getRandomKenBurnsEffect() {
        // Single consistent Ken Burns effect:
        // start "close" and very slowly zoom out. No random pans.
        return {
            startTransform: `scale(1.12) translate(0%, 0%)`,
            endTransform: `scale(1.0) translate(0%, 0%)`
        };
    }
    
    showImage(index, layerIndex) {
        if (this.photos.length === 0) return;
        
        const photo = this.photos[index];
        const img = this.imageElements[layerIndex];
        const otherImg = this.imageElements[1 - layerIndex];
        
        console.log('KenBurnsGallery: Showing image', index, 'on layer', layerIndex, ':', photo);
        
        const effect = this.getRandomKenBurnsEffect();
        // Slow Ken Burns zoom by a factor (user requested 2x slower zoom-out)
        const kenBurnsFactor = 2;
        const displayDuration = this.options.displayDuration * kenBurnsFactor;
        const duration = displayDuration + this.options.transitionDuration;
        
        // Set image - use the photo URL directly
        img.style.backgroundImage = `url("${photo}")`;
        img.style.transform = effect.startTransform;
        img.style.transition = 'none'; // Reset transition first
        img.style.zIndex = '5';
        
        // Force reflow then animate
        img.offsetHeight;
        
        // Set transition and fade in
        img.style.transition = `opacity ${this.options.transitionDuration}ms ease-in-out, transform ${duration}ms ease-out`;
        img.style.opacity = '1';
        
        // Start Ken Burns pan/zoom
        requestAnimationFrame(() => {
            img.style.transform = effect.endTransform;
        });
        
        // Fade out other image
        otherImg.style.opacity = '0';
        otherImg.style.zIndex = '4';
    }
    
    start() {
        if (this.photos.length === 0) {
            console.warn('KenBurnsGallery: No photos to show');
            return;
        }
        
        console.log('KenBurnsGallery: Starting slideshow with', this.photos.length, 'photos');
        this.isPlaying = true;
        this.currentLayer = 0;
        // Keep slideshow timing in sync with the slower Ken Burns zoom
        const kenBurnsFactor = 2;
        const displayDuration = this.options.displayDuration * kenBurnsFactor;
        
        // Show first image immediately
        this.showImage(this.currentIndex, this.currentLayer);
        
        // Set up interval for cycling through multiple photos
        if (this.photos.length > 1) {
            this.intervalId = setInterval(() => {
                this.currentIndex = (this.currentIndex + 1) % this.photos.length;
                this.currentLayer = 1 - this.currentLayer;
                this.showImage(this.currentIndex, this.currentLayer);
            }, displayDuration);
        }
    }
    
    stop() {
        this.isPlaying = false;
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }
    
    hide() {
        this.stop();
        this.imageElements.forEach(img => {
            img.style.opacity = '0';
        });
    }
    
    destroy() {
        this.stop();
        this.imageElements.forEach(img => {
            if (img.parentNode) {
                img.parentNode.removeChild(img);
            }
        });
    }
}


// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { ChampionshipHall, KenBurnsGallery };
}
